import { notifyProgress } from '@billy/design-system'
import { TFunction } from 'i18next'

import { DownloadFormatToMimeType } from '../../../../../../../constants/downloadFormatToMimeType'
import { DownloadFormat } from '../../../../../../../enums/downloadFormat'
import { fetchSalesByCustomerReportDownloadFileData } from '../../../../../../../query-api'
import { SalesReportLocaleIdentifier } from '../../../../../../../types/salesReportLocaleIdentifier'
import { getSalesReportDateRange } from '../../../../../../../utils/getSalesReportDateRange'
import { SALES_REPORT_DOWNLOAD_PROGRESS_MAX } from '../../../constants/salesReportDownloadProgressMax'
import { DownloadWithProgress } from '../../../utils/downloadWithProgress'
import { getSalesReportDownloadNotificationId } from '../../../utils/getSalesReportDownloadNotificationId'

interface DownloadSalesByCustomerReportFileProps {
  endDate?: string
  fileName?: string
  firstFiscalYearStart?: string
  format: DownloadFormat
  locale: SalesReportLocaleIdentifier
  onError?: () => void
  onSuccess?: () => void
  startDate?: string
  t: TFunction
}

export const downloadSalesByCustomerReportFile = async ({
  endDate,
  fileName,
  firstFiscalYearStart,
  format,
  locale,
  onError,
  onSuccess,
  startDate,
  t,
}: DownloadSalesByCustomerReportFileProps): Promise<void> => {
  const { startDate: salesReportStartDate, endDate: salesReportEndDate } = getSalesReportDateRange(
    startDate,
    endDate,
    firstFiscalYearStart,
  )
  const notificationId = getSalesReportDownloadNotificationId(format)

  if (!salesReportStartDate || !salesReportEndDate) {
    notifyProgress({
      id: notificationId,
      title: t('sales_report.download_error.missing_start_or_end_date'),
      variant: 'error',
      progress: SALES_REPORT_DOWNLOAD_PROGRESS_MAX,
      closable: true,
    })
    onError?.()

    return
  }

  const fetchExportData = () =>
    fetchSalesByCustomerReportDownloadFileData({
      startDate: salesReportStartDate,
      endDate: salesReportEndDate,
      format,
      locale,
    })

  const downloader = new DownloadWithProgress({
    fetchExportData,
    fileName,
    mimeType: DownloadFormatToMimeType[format],
    notificationId,
    onError,
    onSuccess,
    t,
  })

  await downloader.start()
}
